import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";

import { ApiError, api, bytes, relativeTime, uptime } from "../api";
import { State } from "./Services";
import { ConsolePanel } from "../components/ConsolePanel";

interface Detail {
  id: string;
  kind: string;
  display_name: string;
  external_id: string;
  node_name: string;
  cluster_name: string;
  location: string;
  status: string;
  power_state: string;
  cpu_cores: number | null;
  cpu_usage_ratio: number | null;
  memory_total_bytes: number | null;
  memory_used_bytes: number | null;
  disk_total_bytes: number | null;
  disk_used_bytes: number | null;
  uptime_seconds: number | null;
  ip_addresses: string[];
  tags: string[];
  capabilities: string[];
  permissions: string[];
  live: boolean;
  state_refreshed_at: string | null;
  plan_id: string | null;
  plan_name: string | null;
  plan_disk_gb: number | null;
}

interface Plan {
  id: string;
  code: string;
  name: string;
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
}

/* Power actions, in the order an operator reaches for them. "Cut power" is
 * named for what it does to the machine rather than what it does to the API,
 * because that is the one on this page that can lose data. */
const ACTIONS = [
  { action: "start", label: "Start", when: (s: string) => s !== "running" },
  { action: "reboot", label: "Reboot", when: (s: string) => s === "running" },
  { action: "shutdown", label: "Shut down", when: (s: string) => s === "running" },
  { action: "stop", label: "Cut power", when: (s: string) => s === "running", danger: true },
] as const;

export function ServiceDetail() {
  const { serviceId = "" } = useParams();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isPending, error: loadError } = useQuery({
    queryKey: ["service", serviceId],
    queryFn: () => api.get<Detail>(`/api/services/${serviceId}`),
    refetchInterval: 15_000,
    // One retry, not the default three. A service page that spends fifteen
    // seconds saying "Loading…" before admitting failure is worse than one
    // that says what went wrong.
    retry: 1,
  });

  const power = useMutation({
    mutationFn: (action: string) =>
      api.post<{ task_id: string }>(`/api/services/${serviceId}/power`, { action }),
    onSuccess: (_result, action) => {
      setError(null);
      // Named for the outcome, matching the button that produced it.
      setNotice(
        { start: "Starting", reboot: "Rebooting", shutdown: "Shutting down", stop: "Powering off" }[
          action
        ] ?? "Working",
      );
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["service", serviceId] }), 2500);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "The action did not complete"),
  });

  if (isPending) return <p className="muted" style={{ paddingTop: "3rem" }}>Loading…</p>;
  if (!data)
    return (
      <p className="notice notice--error" role="alert" style={{ marginTop: "3rem" }}>
        {loadError instanceof ApiError && loadError.status === 404
          ? "This service does not exist, or is not on your account."
          : "This service could not be loaded. Refresh to try again."}
      </p>
    );

  const canPower = data.permissions.includes("service:power") && data.capabilities.includes("power");
  const canConsole =
    data.permissions.includes("service:console") &&
    (data.capabilities.includes("console_vnc") || data.capabilities.includes("console_serial"));

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/services" className="label" style={{ textDecoration: "none" }}>
            ← All services
          </Link>
          <h1>{data.display_name}</h1>
          <p className="row__meta" style={{ marginTop: "0.6rem" }}>
            <span>#{data.external_id}</span>
            <span>{data.node_name}</span>
            <span>{data.cluster_name}</span>
            {data.location && <span>{data.location}</span>}
          </p>
        </div>
        <State value={data.power_state} />
      </div>

      {!data.live && (
        <p className="notice notice--info" role="status">
          {data.status === "missing"
            ? "This service is no longer present on the host. Contact support before making changes."
            : `Showing the last known state from ${relativeTime(data.state_refreshed_at)} — the host is not responding right now.`}
        </p>
      )}
      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      {notice && !error && (
        <p className="notice notice--info" role="status">
          {notice}…
        </p>
      )}

      <div className="stack" style={{ marginTop: "1.25rem" }}>
        <section className="panel">
          <dl className="specs">
            <div>
              <dt className="label">Processor</dt>
              <dd>{data.cpu_cores != null ? `${data.cpu_cores} vCPU` : "—"}</dd>
              {data.cpu_usage_ratio != null && (
                <Meter used={data.cpu_usage_ratio} total={1} />
              )}
            </div>
            <div>
              <dt className="label">Memory</dt>
              <dd>
                {bytes(data.memory_used_bytes)} <span className="muted">of</span>{" "}
                {bytes(data.memory_total_bytes)}
              </dd>
              <Meter used={data.memory_used_bytes} total={data.memory_total_bytes} />
            </div>
            <div>
              <dt className="label">Storage</dt>
              <dd>
                {bytes(data.disk_used_bytes)} <span className="muted">of</span>{" "}
                {bytes(data.disk_total_bytes)}
              </dd>
              <Meter used={data.disk_used_bytes} total={data.disk_total_bytes} />
            </div>
            <div>
              <dt className="label">Uptime</dt>
              <dd>{uptime(data.uptime_seconds)}</dd>
            </div>
            <div>
              <dt className="label">Addresses</dt>
              <dd>{data.ip_addresses.length ? data.ip_addresses.join(", ") : "—"}</dd>
            </div>
          </dl>
        </section>

        {canPower && (
          <section className="panel">
            <div className="spread">
              <div>
                <h2 style={{ marginBottom: "0.15rem" }}>Power</h2>
                <p className="muted" style={{ margin: 0, fontSize: "var(--step--1)" }}>
                  Cutting power is immediate and can lose unsaved data. Shut down asks the
                  operating system to close cleanly first.
                </p>
              </div>
              <div className="btn-group">
                {ACTIONS.filter((a) => a.when(data.power_state)).map((a) => (
                  <button
                    key={a.action}
                    type="button"
                    className={`btn${"danger" in a && a.danger ? " btn--danger" : ""}`}
                    disabled={power.isPending}
                    onClick={() => power.mutate(a.action)}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {canConsole && <ConsolePanel serviceId={data.id} name={data.display_name} />}

        {data.permissions.includes("service:edit") && data.kind === "pve_lxc" && (
          <ChangePlan
            serviceId={data.id}
            planId={data.plan_id}
            planName={data.plan_name}
            diskGb={data.plan_disk_gb}
          />
        )}

        {data.permissions.includes("service:delete") && (
          <DangerZone serviceId={data.id} hostname={data.display_name} />
        )}
      </div>
    </>
  );
}

/* Changing plan.
 *
 * Storage can grow but never shrink, so plans that would reduce it are shown
 * and disabled with the reason attached rather than hidden. A customer who
 * cannot find the smaller plan assumes the page is broken; one who is told why
 * it is unavailable knows what to do instead. */
function ChangePlan({
  serviceId,
  planId,
  planName,
  diskGb,
}: {
  serviceId: string;
  planId: string | null;
  planName: string | null;
  diskGb: number | null;
}) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const catalog = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.get<{ plans: Plan[] }>("/api/catalog"),
  });

  const resize = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>(`/api/services/${serviceId}/resize`, { plan_id: chosen }),
    onSuccess: (result) => {
      setError(null);
      setDone(result.message);
      setChosen(null);
      queryClient.invalidateQueries({ queryKey: ["service", serviceId] });
      queryClient.invalidateQueries({ queryKey: ["usage"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "The plan could not be changed."),
  });

  const plans = catalog.data?.plans ?? [];

  return (
    <section className="panel">
      <h2 style={{ marginBottom: "0.15rem" }}>Plan</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: "var(--step--1)" }}>
        {planName ? `Currently on ${planName}.` : "No plan recorded."} Processor and memory changes
        apply straight away. Storage can only grow.
      </p>

      {done && (
        <p className="notice notice--ok" role="status">
          {done}
        </p>
      )}
      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}

      <div className="ledger" style={{ marginTop: "1rem" }}>
        {plans.map((plan) => {
          const current = plan.id === planId;
          const shrinks = diskGb != null && plan.disk_gb < diskGb;
          const blocked = current || shrinks;
          return (
            <label
              key={plan.id}
              className={`pick${chosen === plan.id ? " pick--on" : ""}${blocked ? " pick--off" : ""}`}
            >
              <input
                type="radio"
                name="plan"
                value={plan.id}
                disabled={blocked}
                checked={chosen === plan.id}
                onChange={() => {
                  setDone(null);
                  setChosen(plan.id);
                }}
              />
              <span>
                <span className="pick__name">{plan.name}</span>
                {current && <span className="pick__note">Your current plan</span>}
                {shrinks && !current && (
                  <span className="pick__note">Storage cannot be reduced from {diskGb} GB</span>
                )}
              </span>
              <span className="pick__spec mono">
                {plan.cpu_cores} vCPU · {bytes(plan.memory_mb * 1024 * 1024)} · {plan.disk_gb} GB
              </span>
            </label>
          );
        })}
      </div>

      <button
        type="button"
        className="btn btn--primary"
        style={{ marginTop: "1rem" }}
        disabled={!chosen || resize.isPending}
        onClick={() => resize.mutate()}
      >
        {resize.isPending ? "Changing…" : "Change plan"}
      </button>
    </section>
  );
}

/* Termination.
 *
 * Kept apart from everything else and requires the server's name typed out.
 * A checkbox gets clicked by reflex; retyping the name of the machine you are
 * about to destroy does not happen by accident. The button stays disabled
 * until the text matches, so the confirmation is not something you can dismiss
 * and then regret. */
function DangerZone({ serviceId, hostname }: { serviceId: string; hostname: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [failed, setFailed] = useState<string | null>(null);

  const terminate = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>(`/api/services/${serviceId}/terminate`, {
        confirm_hostname: typed.trim(),
      }),
    onSuccess: () => navigate("/services", { replace: true }),
    onError: (caught) =>
      setFailed(caught instanceof ApiError ? caught.message : "It could not be removed"),
  });

  if (!open) {
    return (
      <section className="panel">
        <div className="spread">
          <div>
            <h2 style={{ marginBottom: "0.15rem" }}>Terminate</h2>
            <p className="muted" style={{ margin: 0, fontSize: "var(--step--1)" }}>
              Destroys this server and everything on it. The address is held for seven days
              before it can be given to anyone else.
            </p>
          </div>
          <button type="button" className="btn btn--danger" onClick={() => setOpen(true)}>
            Terminate…
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="panel" style={{ borderColor: "var(--halt)" }}>
      <h2 style={{ marginBottom: "0.15rem" }}>Terminate {hostname}</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: "var(--step--1)" }}>
        This cannot be undone. The disk is destroyed with the container.
      </p>

      {failed && (
        <p className="notice notice--error" role="alert" style={{ margin: "1rem 0" }}>
          {failed}
        </p>
      )}

      <label className="field" style={{ marginTop: "1rem" }}>
        <span className="label">Type {hostname} to confirm</span>
        <input
          type="text"
          value={typed}
          autoComplete="off"
          onChange={(e) => setTyped(e.target.value)}
        />
      </label>

      <div className="btn-group">
        <button type="button" className="btn" onClick={() => setOpen(false)}>
          Keep it
        </button>
        <button
          type="button"
          className="btn btn--danger"
          disabled={typed.trim().toLowerCase() !== hostname.toLowerCase() || terminate.isPending}
          onClick={() => terminate.mutate()}
        >
          {terminate.isPending ? "Removing…" : "Terminate permanently"}
        </button>
      </div>
    </section>
  );
}

function Meter({ used, total }: { used: number | null; total: number | null }) {
  if (used == null || !total) return null;
  const ratio = Math.min(used / total, 1);
  const level = ratio > 0.9 ? " meter--critical" : ratio > 0.75 ? " meter--high" : "";
  return (
    <div className={`meter${level}`}>
      <i style={{ width: `${Math.max(ratio * 100, 1)}%` }} />
    </div>
  );
}
